// src/components/FactsActsPanel.js
//
// Record 605 (ruling 138) - CP-3, the screen half. The claimant's change or withdrawal of her filed facts is a
// recorded act, written by the Cloud Function `factsAct` (functions\index.js) and never by this page:
//
//   - callFactsAct() is the ONE place in src that calls httpsCallable(..., 'factsAct'). ClaimFormActions uses it for
//     her post-filing change of the Statement of Claim; this panel uses it for "Withdraw this statement".
//   - the panel lists claims/{id}/factsActs ("changed on DATE", "withdrawn on DATE") with the earlier text kept and
//     shown - firestore.rules lets the claimant, the officers in scope and the administrator read them.
//   - a withdrawal NEVER deletes: the statement stays on the claim, marked withdrawn (factsWithdrawn.statementOfClaim).
//
// WHO IS OFFERED THE ACT - factsClaimant() in src\data\factsActs.js, the same function the server decides with.
// Nothing here runs on load: the callable is reached only from a click.
import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { WITHDRAWN, factsClaimant, factText, isWithdrawn, actLine, valueText, REASON_MAX } from '../data/factsActs';

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', ok: '#5FB878', text: '#DCE7F2', bad: '#E06C6C', panel: '#0A1628' };

// The caller as factsClaimant() reads it, from the page's user object.
export function factsCaller(user) {
  const u = user || {};
  return {
    uid: String(u.uid || (auth.currentUser && auth.currentUser.uid) || ''),
    role: u.isAdmin ? 'admin' : String(u.role || ''),
    email: String(u.email || (auth.currentUser && auth.currentUser.email) || ''),
  };
}

// THE ONE CALL SITE. Returns the function's answer, or throws with its message.
export async function callFactsAct(payload) {
  const fn = httpsCallable(getFunctions(), 'factsAct');
  const r = await fn(payload);
  return (r && r.data) || {};
}

export default function FactsActsPanel({ claimId, claim, user, onPatched }) {
  const [acts, setActs] = useState([]);
  const [readErr, setReadErr] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const isDraft = String((claim && claim.status) || '') === 'draft';

  useEffect(() => {
    if (!claimId || isDraft) return undefined;
    const q = query(collection(db, 'claims', claimId, 'factsActs'), orderBy('at', 'asc'));
    return onSnapshot(q,
      (snap) => { setActs(snap.docs.map((d) => ({ ...d.data(), docId: d.id }))); setReadErr(''); },
      (e) => setReadErr('Could not read the record of changes to the facts: ' + ((e && (e.code || e.message)) || e) + '.'));
  }, [claimId, isDraft]);

  if (!claim || !claimId || isDraft) return null;

  const who = factsClaimant(claim, factsCaller(user));
  const stmt = factText(claim, 'statementOfClaim');
  const approved = !!(claim.statementOfClaim && String(claim.statementOfClaim.approvedBy || '').trim());
  const withdrawn = isWithdrawn(claim, 'statementOfClaim');
  const mayWithdraw = who.may && approved && !!stmt && !withdrawn;

  // ONLY EVER CALLED FROM A CLICK (the Confirm button below).
  const withdraw = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const d = await callFactsAct({ claimId, kind: WITHDRAWN, field: 'statementOfClaim', reason: reason.trim() });
      if (onPatched && d.view) onPatched(d.view);
      setMsg('Withdrawn. Your statement stays on the claim, marked withdrawn, and your Local and General Chairpersons have been told.'
        + (Array.isArray(d.unaddressed) && d.unaddressed.length ? ' Not addressed: ' + d.unaddressed.join('; ') + '.' : ''));
      setConfirming(false); setReason('');
    } catch (e) {
      setErr('Not recorded: ' + ((e && e.message) || String(e)) + '. Nothing was changed.');
    } finally { setBusy(false); }
  };

  if (!acts.length && !readErr && !mayWithdraw && !withdrawn) return null;

  const box = { border: '1px solid ' + C.line, background: C.panel, padding: '10px 12px', marginBottom: 10, fontSize: 12, color: C.text };
  return (
    <div style={box}>
      <div style={{ fontSize: 10, color: C.accent, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
        Changes to the claimant&rsquo;s facts
      </div>
      {readErr && <div style={{ color: C.bad, marginBottom: 6 }}>&#9888; {readErr}</div>}
      {err && <div style={{ color: C.bad, marginBottom: 6 }}>&#9888; {err}</div>}
      {msg && <div style={{ color: C.ok, marginBottom: 6 }}>{msg}</div>}
      {withdrawn && <div style={{ color: C.accent, marginBottom: 6 }}>The Statement of Claim has been withdrawn by the claimant. Its text is kept below, as it was.</div>}

      {acts.map((a) => (
        <div key={a.docId} style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line }}>
          <div style={{ fontWeight: 700, color: a.kind === WITHDRAWN ? C.accent : C.text }}>{actLine(a)}</div>
          {a.reason ? <div style={{ color: C.dim }}>Reason: {a.reason}</div> : null}
          {valueText(a.before) ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ color: C.dim, fontSize: 11 }}>{a.kind === WITHDRAWN ? 'The text withdrawn (kept):' : 'Before:'}</div>
              <div style={{ whiteSpace: 'pre-wrap', color: C.dim }}>{valueText(a.before)}</div>
            </div>
          ) : <div style={{ color: C.dim, fontSize: 11, marginTop: 4 }}>Before: nothing was recorded there.</div>}
          {a.kind !== WITHDRAWN && valueText(a.after) ? (
            <div style={{ marginTop: 4 }}>
              <div style={{ color: C.dim, fontSize: 11 }}>After:</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{valueText(a.after)}</div>
            </div>
          ) : null}
        </div>
      ))}

      {mayWithdraw && (
        <div style={{ marginTop: 8 }}>
          {confirming ? (
            <div>
              <div style={{ marginBottom: 6 }}>Withdrawing your statement does not erase it: it stays on your claim, marked withdrawn, and your Local and General Chairpersons are told. The claim cannot be sent without a statement you stand behind.</div>
              <label className="form-label" htmlFor="facts-withdraw-reason" style={{ color: C.dim }}>Your reason (optional)</label>
              <textarea id="facts-withdraw-reason" className="form-textarea" rows={3} maxLength={REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />
              <button className="btn btn-primary btn-sm" style={{ marginRight: 8, marginTop: 6 }} disabled={busy} onClick={withdraw}>{busy ? 'Recording…' : 'Confirm - withdraw this statement'}</button>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={() => { setConfirming(false); setErr(''); }}>Cancel</button>
            </div>
          ) : (
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setMsg(''); setConfirming(true); }}>Withdraw this statement</button>
          )}
        </div>
      )}
    </div>
  );
}
